<template>
  <div>
    <!-- Affichage d'une barre de progression pendant le chargement des données -->
    <load-progress :condition="!dataLoaded" label="Chargement des données"></load-progress>
    <!-- Affichage du tree view et des éditeurs selon l'objet sélectionné -->

    <div v-if="dataLoaded">
      <section-selection-abonnement v-if="showSelectionAbo"
                                    :listItems="listItems"
                                    :subscriptionTypeSelected="initialSubscriptionType.id"
                                    :ownerId="owner.objId"
                                    @closeDialog="closeDialog"
                                    @offreSelectionner="offreSelectionner"
                                    @goToDialog="goToDialog" />

      <section-subscription-changement-abonnement v-if="showPaiementAbo"
                                                  :subscription-required="subscriptionRequired"
                                                  :owner="owner"
                                                  :subscriptionTypeSelected="subscriptionTypeSelected"
                                                  :nouvelleOffreSelectionner="nouvelleOffreSelectionner"
                                                  @closeDialog="closeDialog"
                                                  @goToSelection="goToSelection"
                                                  @goToConfirmation="goToConfirmation">
      </section-subscription-changement-abonnement>

      <section-subscription-confirmation v-if="showConfirmation"
                                         :owner="owner"
                                         :logoDisplay="true"
                                         :logo="logo"
                                         :title="confirmationTitle"
                                         :text="confirmationText"
                                         :cardDisplay="true"
                                         :subscriptionType="offre"
                                         :rightLabel="rightLabel"
                                         :featureList="featureList"
                                         :labelUrl="labelUrl"
                                         :buttons="buttons"
                                         :show-close="!declarationInformation"
                                         @closeDialog="closeDialog">
      </section-subscription-confirmation>

      <section-subscription-confirmation
        v-if="showDeclarations && declarationInformation"
        :logo-display="true"
        :logo="logo"
        title="Vos déclarations sociales"
        :card-display="false"
        :buttons="declarationButtons"
        @close-dialog="closeDialog">
        <p>
          Ovatio va se charger de réaliser toutes vos déclarations mensuelles obligatoires auprès des organismes sociaux,
          dès {{ declarationInformation.month }}.
        </p>
        <p>
          Vous souhaitez un démarrage anticipé ?
          <a :href="declarationContactUrl">Nous contacter</a>
        </p>
      </section-subscription-confirmation>

      <section-subscription-confirmation v-if="showDialog"
                                         :owner="owner"
                                         :logoDisplay="true"
                                         :logo="dialogLogo"
                                         :title="dialogTitle"
                                         :text="dialogText"
                                         :cardDisplay="false"
                                         :buttons="dialogButtons"
                                         @closeDialog="closeDialog">
      </section-subscription-confirmation>
    </div>
  </div>
</template>

<script lang="ts">
  import LoadProgress from '@/framework/base/components/LoadProgress.vue';
  import { storeEtablissement, storePaymentMeans, storeSubscription, storeSubscriptionType } from '@/generated/stores';
  import { SubscriptionRequired } from '@/generated/classes/SubscriptionType';
  import Owner from '@/generated/classes/Owner';
  import Subscription from '@/generated/classes/Subscription';
  import { defineComponent, type PropType } from "vue";
  import SectionSelectionAbonnement from "@/custom/sections/SectionSelectionAbonnement.vue";
  import SectionSubscriptionChangementAbonnement from "@/custom/sections/SectionSubscriptionChangementAbonnement.vue";
  import ListData, { ColumnFilter, ListFilter } from "@/extend/classes/ListData.ts";
  import SectionSubscriptionConfirmation from "@/custom/sections/SectionSubscriptionConfirmation.vue";
  import SubscriptionType from "@/extend/classes/SubscriptionType.ts";
  import SectionModifPaie from "@/custom/sections/SectionModifPaie.vue";
  import Button from "@/framework/base/classes/Button.ts";
  import DialogHubManager from "@/framework/dialog/helpers/DialogHubManager.ts";
  import Etablissement from "@/generated/classes/Etablissement.ts";
  import { DateFormat } from '@/framework/base/helpers/DateFormat';
  import Synchro98 from '../../generated/classes/Synchro98';
  import { declarationContactUrl, type SubscriptionDeclarationInformation } from '@/custom/helpers/subscriptionDeclaration';

  export default defineComponent({
    components: {
      SectionModifPaie,
      SectionSubscriptionConfirmation,
      SectionSubscriptionChangementAbonnement,
      SectionSelectionAbonnement,
      LoadProgress
    },
    props: {
      hideTitle: {
        type: Boolean
      },
      owner: {
        type: Object as PropType<Owner>,
        required: true
      },
      subscriptionRequired: {
        type: Object as PropType<SubscriptionRequired>,
        required: true
      },
      toggleReset: {
        type: Boolean,
      },
      filters: {
        type: Object as PropType<ColumnFilter[]>,
      },
    },
    data() {
      const listSubscriptionType: SubscriptionType[] = [];
      return {
        nbPaiesUsed: 0,
        subscriptionTypeId: 0,
        showSelectionAbo: true,
        showPaiementAbo: false,
        showConfirmation: false,
        showDialog: false,
        showDeclarations: false,
        declarationInformation: null as SubscriptionDeclarationInformation | null,
        reversed: false,
        reset: true,
        listSubscriptionType,
        listUpdated: false,
        notOrdered: false,
        minSubscriptionId: 0,
        terminateId: 0,
        etablissementId: 0,
        aboMeansId: 0,
        etablissement: new Etablissement(),
        initialSubscriptionType: new SubscriptionType(),
        containerWidth: 1200
      };
    },
    created() {
      DialogHubManager.waitAnswer('resilier', this.finaliseCancel);
      const allData = new ListFilter();
      allData.pageLimit = -1;
      // Load and register events on used server classes
      storeEtablissement.registerEtablissement(allData);
      storePaymentMeans.registerPaymentMeans(allData);
      storeSubscriptionType.registerSubscriptionType(allData);
      storeSubscription.registerSubscription(allData);
      const subscription = storeSubscription.listSubscription.find(a => a.ownerId == this.owner.objId && a.ownerType == this.owner.objType);
      if (subscription) {
        this.subscriptionTypeId = subscription.subscriptionTypeId;
        this.aboMeansId = subscription.aboMeansId;
        this.etablissementId = subscription.ownerId;
        this.nbPaiesUsed = subscription.nbPaiesUsed;
        this.initialSubscriptionType = storeSubscriptionType.getSubscriptionType(this.subscriptionTypeId);
      } else {
        this.etablissementId = this.owner.objId;
      }
    },
    computed: {
      confirmationTitle(): string {
        let headerTitle = '';
        if (this.subscription.dateResiliation != null) {
          headerTitle = 'Nous sommes désolé de vous voir résilier l’offre ' + this.formattedTitle(this.nouvelleOffreSelectionner) + ' ?';
        } else if (this.reversed && this.nouvelleOffreSelectionner) {
          headerTitle = 'Vous êtes sûr de vouloir descendre à l’offre ' + this.formattedTitle(this.nouvelleOffreSelectionner) + ' ?';
        } else {
          headerTitle = 'Félicitation, vous avez opté pour la formule ' + this.formattedTitle(this.nouvelleOffreSelectionner);
        }

        return headerTitle;
      },
      confirmationText() {
        let text = '';
        const nextReglement = new Date(this.subscription.dateDebut);

        while (nextReglement < new Date()) {
          nextReglement.setMonth(nextReglement.getMonth() + 1);
        }

        if (this.subscription.dateResiliation != null) {
          text = "<br><br>Afin de conserver votre compte et vos données vous pouvez choisir de passer à l'offre PRELUDE à l'issue de votre engagement le " + DateFormat.getDateLabel(this.subscription.dateResiliation) + ".";
        } else if (this.reversed && this.nouvelleOffreSelectionner) {
          text += 'En descendant à l’offre ' + this.formattedTitle(this.nouvelleOffreSelectionner) + ' vous perdrez tous vos avantages disponibles avec l’offre ' + this.formattedTitle(this.initialSubscriptionType) + '.';
          text += '<br><br> Ce changement d’abonnement vous engage pour une nouvelle durée de 12 mois';
        } else {
          text = 'Découvrez tous les avantages dont vous bénéficiez.';
        }
        return text;
      },
      dataLoaded(): boolean {
        const res = storeSubscription.subscriptionLoaded &&
          storeSubscriptionType.subscriptionTypeLoaded &&
          storePaymentMeans.paymentMeansLoaded &&
          storeEtablissement.etablissementLoaded;
        return res;
      },
      subscription(): Subscription {
        let res = new Subscription();
        const subscription = storeSubscription.listSubscription.find(a => a.ownerId == this.owner.objId && a.ownerType == this.owner.objType);
        if (subscription) {
          res = subscription;
        }
        return res;
      },
      subscriptionTypeSelected: {
        get(): number {
          return this.subscriptionTypeId;
        },
        set(item: number) {
          this.subscriptionTypeId = item;
        },
      },
      items(): SubscriptionType[] {
        if (this.reset != this.toggleReset) {
          this.loadData()
        }
        return this.listSubscriptionType;
      },
      listItems(): SubscriptionType[] {
        const res: SubscriptionType[] = [];
        this.notOrdered = true;
        this.items.sort((a, b) => a.mensuel - b.mensuel).forEach((subscriptionType) => {
          // Can subsribe if maxpaies & != prelude if has subscription
          subscriptionType.canSubscribe = (subscriptionType.maxPaies == -1 || subscriptionType.maxPaies >= this.nbPaiesUsed) &&
            (this.subscription.id == 0 || subscriptionType.mensuel > 0);
          res.push(subscriptionType);
        });
        return res;
      },
      nouvelleOffreSelectionner() {
        return this.listItems.find(a => a.id == this.subscriptionTypeId);
      },
      offre() {
        if (!this.reversed) {
          return this.nouvelleOffreSelectionner;
        } else {
          return this.initialSubscriptionType;
        }
      },
      logo() {
        if (!this.reversed) {
          return new URL('@/assets/SVG/AboUp.svg', import.meta.url).href;
        } else {
          return new URL('@/assets/SVG/AboDown.svg', import.meta.url).href;
        }
      },
      rightLabel() {
        if (!this.reversed) {
          return 'Vous avez accès à :';
        } else {
          return 'Vous perdez :';
        }
      },
      featureList() {
        const list: string[] = [];
        let selected;

        if (!this.reversed) {
          selected = this.listItems.find(a => a.id == this.subscriptionTypeSelected);
        } else {
          selected = this.listItems.find(a => a.id == this.initialSubscriptionType.id);
        }

        if (selected?.hasLicenceFollow) {
          list.push('Licence');
        }

        if (selected?.hasConvention) {
          list.push('Conventions');
        }

        if (selected?.hasCustomContrat) {
          list.push('Contrats de travail personnalisés');
        }

        if (selected?.hasSignatory) {
          list.push('La signature électronique');
        }

        return list;
      },
      labelUrl() {
        if (!this.reversed) {
          return 'En savoir plus sur l\'offre ' + this.formattedTitle(this.nouvelleOffreSelectionner);
        } else {
          return 'Plus de détails sur nos offres d’abonnement';
        }
      },
      buttons(): Button[] {
        const buttons: Button[] = [];
        if (!this.reversed) {
          const button = new Button();
          button.show = true;
          button.label = this.declarationInformation ? 'Suivant' : 'Ok';
          button.disabled = false;
          button.outlined = false;
          button.callback = this.declarationInformation ? this.goToDeclarations : this.fermerDialog;
          buttons.push(button);
          if (this.subscription.dateResiliation != null && !this.subscription.resiliationPrelude) {
            const button = new Button();
            button.show = true;
            button.label = 'Passer à l\'offre PRELUDE';
            button.disabled = false;
            button.outlined = true;
            button.callback = this.resilierPrelude;
            buttons.push(button);
          }
        } else {
          const button = new Button();
          button.show = true;
          button.label = 'Rester à l’offre ' + this.formattedTitle(this.initialSubscriptionType);
          button.disabled = false;
          button.outlined = false;
          button.callback = this.fermerDialog;
          buttons.push(button);

          const button2 = new Button();
          button2.show = true;
          button2.label = 'Descendre à l’offre ' + this.formattedTitle(this.nouvelleOffreSelectionner);
          button2.disabled = false;
          button2.outlined = true;
          button2.callback = this.goDownOffer;
          buttons.push(button2);
        }
        return buttons;
      },
      declarationContactUrl(): string {
        return this.declarationInformation ? declarationContactUrl(this.declarationInformation) : '';
      },
      declarationButtons(): Button[] {
        const button = new Button();
        button.show = true;
        button.label = 'Fermer';
        button.disabled = false;
        button.outlined = false;
        button.callback = this.fermerDialog;
        return [button];
      },
      dialogLogo() {
        return new URL('@/assets/SVG/AboDown.svg', import.meta.url).href;
      },
      dialogTitle() {
        if (this.subscription.dateResiliation) {
          return 'Abonnement résilié';
        } else {
          return 'Demande de résiliation';
        }
      },
      dialogText() {
        let text = '';
        if (this.subscription.dateResiliation && this.subscriptionTypeSelected == this.initialSubscriptionType.id) {
          if (this.subscription.finPreavis < new Date()) {
            text = "Votre abonnement est résilié et prendra fin le " + DateFormat.getDateLabel(this.subscription.dateResiliation) + ".";
          } else {
            text = "Vous avez demandé la résiliation de votre abonnement. Elle sera effective, compte tenu du préavis, le " + DateFormat.getDateLabel(this.subscription.finPreavis) + ".";
            if (!this.subscription.resiliationPrelude) {
              text += "<br><br>Afin de conserver votre compte et vos données vous pouvez choisir de passer à l'offre PRELUDE à l'issue de votre engagement le " + DateFormat.getDateLabel(this.subscription.dateResiliation) + ".";
            }
          }
        } else {
          const fin = new Date(this.nouvelleOffreSelectionner.id != this.subscription.subscriptionTypeId ? new Date() : this.subscription.dateDebut);
          const finPreavis = new Date(fin);
          if (this.initialSubscriptionType.mensuel > 0) {
            fin.setFullYear(fin.getFullYear() + 1);
          }
          const preavis = new Date();
          preavis.setDate(preavis.getDate() + 15);
          while (fin < preavis) {
            fin.setMonth(fin.getMonth() + 1);
          }
          while (finPreavis < preavis) {
            finPreavis.setMonth(finPreavis.getMonth() + 1);
          }
          const perteDonnees = new Date(fin);
          perteDonnees.setDate(perteDonnees.getDate() + 30);
          text = "Votre demande de résiliation sera effective, compte tenu du préavis, le " + DateFormat.getDateLabel(finPreavis) + ". Vous pouvez annuler votre résiliation jusqu'à cette date." +
            "<br><br>Votre abonnement " + this.initialSubscriptionType.label + " sera résilié le " + DateFormat.getDateLabel(fin) +
            "<br>Votre compte et vos données seront supprimés le " + DateFormat.getDateLabel(perteDonnees) + "." +
            "<br><br>Un e-mail de confirmation vous sera envoyé."
        }
        return text;
      },
      dialogButtons(): Button[] {
        const buttons: Button[] = [];
        {
          const button = new Button();
          button.show = true;
          button.label = 'Annuler';
          button.disabled = false;
          button.outlined = false;
          button.callback = this.fermerDialog;
          if (this.subscription.dateResiliation != null) {
            button.label = 'Fermer';
          }
          buttons.push(button);
        }
        {
          const button = new Button();
          button.show = true;
          button.label = 'Résilier';
          button.disabled = false;
          button.outlined = true;
          button.callback = this.resilier;
          if (this.subscription.dateResiliation != null) {
            if (this.subscription.finPreavis < new Date()) {
              button.label = 'Souscrire';
            } else {
              button.label = 'Annuler ma résiliation';
            }
          }
          buttons.push(button);
        }
        if (this.subscription.dateResiliation != null && !this.subscription.resiliationPrelude) {
          const button = new Button();
          button.show = true;
          button.label = 'Passer à l\'offre PRELUDE';
          button.disabled = false;
          button.outlined = true;
          button.callback = this.resilierPrelude;
          buttons.push(button);
        }
        return buttons;
      },
    },

    methods: {
      formattedTitle(option: SubscriptionType) {
        return option.label.charAt(0).toUpperCase() + option.label.slice(1).toLowerCase()
      },
      fermerDialog(): Promise<void> {
        return new Promise((resolve) => {
          this.closeDialog();
          resolve();
        });
      },
      resilierPrelude(): Promise<void> {
        return new Promise((resolve) => {
          this.subscription.resiliationPrelude = true;
          Subscription.cancel(this.subscription).then((res: boolean) => {
            this.closeDialog();
            resolve();
          });
        });
      },
      resilier(): Promise<void> {
        if (new Date() < this.subscription.finPreavis || this.subscription.dateResiliation == null) {
          return new Promise((resolve) => {
            Subscription.cancel(this.subscription).then((res: boolean) => {
              if (res) {
                this.closeDialog();
              }
              resolve();
            });
          });
        } else {
          this.goToPaiement();
        }
      },
      finaliseCancel(res: boolean) {
        if (res) {
          this.goToConfirmation();
        } else {
          this.closeDialog();
        }
      },
      goDownOffer(): Promise<void> {
        return new Promise((resolve) => {
          this.goToPaiement();
          this.reversed = false;
          resolve();
        });
      },
      loadData() {
        this.reset = this.toggleReset;
        const listFilters = new ListFilter();
        listFilters.columnsFilter = this.filters;
        SubscriptionType.list(listFilters)
          .then((data: ListData) => {
            this.listSubscriptionType = data.reccords as SubscriptionType[];
            this.etablissement = storeEtablissement.getEtablissement(this.owner.objId);
            if (this.etablissement.suggestedSubscriptionTypeId > 0) {
              this.listSubscriptionType = this.listSubscriptionType.filter(a => a.mensuel > 0);
              this.containerWidth = 910;
              this.$emit('setSize', false, true, this.containerWidth);
            } else {
              this.containerWidth = 1200;
            }
            this.listUpdated = true;
          }).catch(() => {
            //Catch empty list
          });
      },
      closeDialog() {
        this.etablissement.suggestedSubscriptionTypeId = 0;
        Synchro98.unsetSuggested(this.etablissement.id);
        this.$emit('closeDialog');
      },
      offreSelectionner(offreSelectioner: SubscriptionType) {
        this.subscriptionTypeSelected = offreSelectioner.id;
        if (offreSelectioner.id >= this.initialSubscriptionType.id) {
          this.reversed = false;
          this.goToPaiement();
        } else if (offreSelectioner.id < this.initialSubscriptionType.id) {
          this.reversed = true;
          this.goToConfirmation();
        }
      },
      goToPaiement() {
        this.showDeclarations = false;
        this.declarationInformation = null;
        this.$emit('setSize', false, false);
        this.showSelectionAbo = false;
        this.showPaiementAbo = true;
        this.showConfirmation = false;
        this.showDialog = false;
      },
      goToSelection(success: boolean) {
        this.showDeclarations = false;
        this.declarationInformation = null;
        if (!success) {
          const subscription = storeSubscription.listSubscription.find(a => a.ownerId == this.owner.objId && a.ownerType == this.owner.objType);
          if (subscription) {
            this.subscriptionTypeId = subscription.subscriptionTypeId;
          } else {
            this.subscriptionTypeId = 0;
          }
        }
        this.$emit('setSize', false, true, this.containerWidth);
        this.showSelectionAbo = true;
        this.showPaiementAbo = false;
        this.showConfirmation = false;
        this.showDialog = false;
      },
      goToConfirmation(information: SubscriptionDeclarationInformation | null = null) {
        this.declarationInformation = information;
        this.showDeclarations = false;
        this.$emit('setSize', false, false);
        this.showSelectionAbo = false;
        this.showPaiementAbo = false;
        this.showConfirmation = true;
        this.showDialog = false;
      },
      goToDeclarations(): Promise<void> {
        this.showConfirmation = false;
        this.showDeclarations = true;
        return Promise.resolve();
      },
      goToDialog() {
        this.showDeclarations = false;
        this.declarationInformation = null;
        this.$emit('setSize', false, false);
        this.showSelectionAbo = false;
        this.showPaiementAbo = false;
        this.showConfirmation = false;
        this.showDialog = true;
      },
    }
  })

</script>
